// Administrations — spec ch.06: "Company table, progress, connection health and
// add administration … Tenant/admin model, grants, external ID mapping and
// onboarding. A new administration starts in review mode."
//
// The office's administrations, what each one is connected to, how fresh that
// connection is, and how much work is waiting in it. Four rules, and three of
// them are about what this screen does NOT show.
//
// **The list is the workspace's, not a second copy of it.** `GET
// /administrations` is read in exactly one place in this app —
// state/workspace.tsx — because the header's administration selector and this
// table have to agree. A screen that fetched the list again could render a row
// the selector does not offer, or offer a selection the table does not list, and
// a reviewer would have no way to tell which was right. The list is not filtered
// by the selected administration either: this is the screen you go to in order
// to CHANGE that selection, and hiding the other rows would make it useless.
//
// **The work column is a count the server computed.** One `GET
// /work?administrationId=…` per administration, which is one request per row and
// is worth saying out loud: there is no endpoint that groups open work by
// administration, and the alternative — deriving per-administration numbers from
// one page of the unfiltered queue — would produce a number nobody could
// reproduce, which is the same rule Today.tsx follows for its tiles. A row whose
// count could not be read says so rather than showing a zero.
//
// **There is no progress column.** The demo shows onboarding progress per
// office; ch.06 wants it derived from "milestone records derived from completed
// checks and evidence", and nothing records one. An invented bar here would be
// the same fabrication ch.06 forbids for Fleur's progress.
//
// **There is no "add administration" button.** Creating one is an onboarding
// step — a tenant record, a connection authorisation, a customer-approved
// external mapping — and no endpoint exposes any of it. V07 asks for demo-only
// controls to be absent or replaced by real workflows rather than left as
// buttons that do nothing, so what is here instead is a line saying where an
// administration comes from.

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, request, tenantPath } from '../api/client.ts';
import type { Administration, AdministrationFigures, WorkQueue } from '../api/types.ts';
import { AdministrationSettings } from '../components/AdministrationSettings.tsx';
import { Metric } from '../components/Metric.tsx';
import { useRead } from '../state/useRead.ts';
import type { ScreenId } from '../components/Shell.tsx';
import { useWorkspace } from '../state/workspace.tsx';
import * as format from '../format.ts';

/**
 * ch.02's four connection states, as something a reviewer can act on.
 *
 * The enum value stays canonical on the wire — contracts/http-api.md:
 * "Localise presentation without changing canonical enum values" — and this is
 * only its presentation. `reauthorisation_required` is the one that matters:
 * it is not a fault to report, it is a thing the office has to go and do, and
 * ch.06 lists it among the states the UI must show explicitly.
 */
const CONNECTION_HEALTH: Readonly<Record<string, { label: string; tone: string; detail: string }>> = {
  healthy: {
    label: 'Connected',
    tone: 'ok',
    detail: 'The last check reached the accounting package.',
  },
  degraded: {
    label: 'Degraded',
    tone: 'warn',
    detail: 'Reachable, but something is failing. Work may queue for longer than usual.',
  },
  reauthorisation_required: {
    label: 'Reauthorisation needed',
    tone: 'danger',
    detail: 'Someone has to authorise the connection again before anything can be sent.',
  },
  disabled: {
    label: 'Disabled',
    tone: 'neutral',
    detail: 'This connection is switched off. Nothing will be sent from it.',
  },
};

function health(value: string): { label: string; tone: string; detail: string } {
  return (
    CONNECTION_HEALTH[value] ?? {
      // A state this build does not know about is shown as itself rather than
      // as "unknown": the canonical value is more use to whoever has to ask
      // about it than a word this screen made up.
      label: value.replace(/_/g, ' '),
      tone: 'neutral',
      detail: 'This connection state is not one this screen knows about.',
    }
  );
}

/** What is waiting in one administration, or why that could not be answered. */
interface WorkLoad {
  readonly counts: WorkQueue['counts'] | null;
  readonly error: string | null;
}

export interface AdministrationsScreenProps {
  readonly onNavigate: (screen: ScreenId) => void;
}

export function AdministrationsScreen({ onNavigate }: AdministrationsScreenProps): ReactNode {
  const workspace = useWorkspace();
  const { token, administrations, administrationId } = workspace;
  const tenantId = workspace.membership.tenantId;

  const [load, setLoad] = useState<Readonly<Record<string, WorkLoad>> | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadWork = useCallback(async () => {
    const collected: Record<string, WorkLoad> = {};
    for (const administration of administrations) {
      try {
        const body = await request<WorkQueue>(
          tenantPath(tenantId, `/work?administrationId=${encodeURIComponent(administration.id)}`),
          { token },
        );
        collected[administration.id] = { counts: body.counts, error: null };
      } catch (caught) {
        // One administration failing must not blank the others — the same rule
        // the bank-matching screen follows. A reviewer with five offices should
        // still see the four that answered.
        collected[administration.id] = {
          counts: null,
          error:
            caught instanceof ApiError && caught.isAbsent
              ? 'No longer available to your session'
              : 'Could not be read',
        };
      }
    }
    setLoad(collected);
  }, [tenantId, token, administrations]);

  useEffect(() => {
    void loadWork();
  }, [loadWork]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      // The workspace owns the list, so this asks IT to re-read
      // GET /administrations. The header's selector updates with the table,
      // because there is only one list.
      await workspace.refreshAdministrations();
      await loadWork();
    } finally {
      setRefreshing(false);
    }
  }, [workspace, loadWork]);

  const connected = administrations.filter(
    (administration) => administration.connection?.health === 'healthy',
  );
  const needingAttention = administrations.filter(
    (administration) =>
      administration.connection === null || administration.connection.health !== 'healthy',
  );
  const paused = administrations.filter((administration) => administration.paused);

  // Counted by the server per administration, under the same grants as the
  // list above; the tiles add those rows up, never a page of items.
  const figures = useRead<AdministrationFigures>('/insights/administrations', 'the administration figures');
  const rows = figures.data?.administrations ?? null;
  const total = (pick: (row: AdministrationFigures['administrations'][number]) => number) =>
    rows === null ? undefined : rows.reduce((sum, row) => sum + pick(row), 0);
  const bankLines = total((row) => row.bankLines);
  const allocated = total((row) => row.bankLinesAllocated);
  const figureFor = new Map((rows ?? []).map((row) => [row.administrationId, row]));

  return (
    <>
      <div className="heading">
        <h1>Administrations</h1>
        <p>
          One workspace over the accounting software each office already uses. You see the
          administrations you have been granted.
        </p>
      </div>

      <div className="metrics">
        <Metric
          icon="building"
          label="Administrations"
          value={administrations.length}
          caption={
            workspace.membership.administrationAccess === 'all'
              ? 'Every one in this office'
              : 'Granted to you'
          }
        />
        <Metric
          icon="file"
          label="Open documents"
          value={total((row) => row.openDocuments)}
          loading={figures.loading}
          caption={figures.error ?? 'Not yet approved, excluded or imported'}
        />
        <Metric
          icon="check"
          label="Confirmed this month"
          value={total((row) => row.confirmedThisMonth)}
          loading={figures.loading}
          caption={
            figures.error ??
            (rows === null ? 'Bookings the package confirmed' : `${total((row) => row.confirmedLastMonth)} last month`)
          }
        />
        <Metric
          icon="swap"
          label="Bank lines allocated"
          value={
            bankLines === undefined || allocated === undefined
              ? undefined
              : bankLines === 0
                ? '—'
                : `${Math.round((allocated / bankLines) * 100)}%`
          }
          loading={figures.loading}
          caption={
            figures.error ??
            (bankLines === undefined
              ? 'Of imported bank lines'
              : bankLines === 0
                ? 'No bank lines imported yet'
                : `${allocated} of ${bankLines} imported lines`)
          }
        />
      </div>

      <div className="card">
        <div className="card-head">
          <div>
            <h2>Your administrations</h2>
            <p>
              Work waiting, confirmed bookings over ten weeks, connection health and the last
              successful sync · {connected.length} connected · {needingAttention.length} need
              attention · {paused.length} paused
            </p>
          </div>
          <button type="button" className="btn" disabled={refreshing} onClick={() => void refresh()}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {administrations.length === 0 ? (
          <div className="empty">
            <h3>No administrations</h3>
            <p>
              This session has no administration in this office. An office owner grants access per
              administration; ask for one rather than expecting this list to fill itself.
            </p>
          </div>
        ) : (
          <div className="admin-cards">
                {administrations.map((administration) => (
                  <AdministrationRow
                    key={administration.id}
                    administration={administration}
                    work={load?.[administration.id]}
                    figures={figureFor.get(administration.id)}
                    figuresError={figures.error}
                    selected={administration.id === administrationId}
                    onOpenWork={() => {
                      // The selection changes because a reviewer pressed a
                      // control that says it will — V03's "without changing
                      // administration context accidentally" is about accidents,
                      // not about deliberate navigation.
                      workspace.selectAdministration(administration.id);
                      onNavigate('work');
                    }}
                  />
                ))}
          </div>
        )}

        <div className="card-foot">
          {/* No "Add administration" button. See the note at the top of the
              file: creating one is an onboarding step with no endpoint, and V07
              asks for a real workflow or nothing. */}
          An administration is created during onboarding, together with its connection and the
          customer-approved mapping to the accounting package. There is no endpoint for it yet, so it
          is not something this screen can do.
        </div>
      </div>

      <div className="card section-space">
        <div className="card-head">
          <h2>What this screen cannot tell you yet</h2>
        </div>
        <div className="card-body">
          {/* Named rather than left as a gap a reviewer discovers. Each of these
              needs an endpoint that does not exist; see NEXT.md. */}
          <ul className="note-list">
            <li>
              <b>Onboarding progress.</b> ch.06 wants it derived from milestone records with
              evidence. Nothing records one, so there is no progress bar here rather than an
              invented one.
            </li>
            <li>
              <b>Whether an administration is in review mode.</b> Every booking waits for a person
              unless a release says otherwise, and release records are not exposed by any endpoint.
              What is shown is what the API answers: paused, or not.
            </li>
            <li>
              <b>The external administration reference.</b> The mapping to the accounting package's
              own identifier exists in the database and is not in this response.
            </li>
          </ul>
        </div>
      </div>
    </>
  );
}

function AdministrationRow({
  administration,
  work,
  figures,
  figuresError,
  selected,
  onOpenWork,
}: {
  readonly administration: Administration;
  readonly work: WorkLoad | undefined;
  readonly figures: AdministrationFigures['administrations'][number] | undefined;
  readonly figuresError: string | null;
  readonly selected: boolean;
  readonly onOpenWork: () => void;
}): ReactNode {
  const workspace = useWorkspace();
  const connection = administration.connection;
  const state = connection === null ? null : health(connection.health);
  const open =
    work?.counts === undefined || work.counts === null
      ? null
      : Object.values(work.counts).reduce((total, n) => total + n, 0);

  const allocatedShare =
    figures === undefined || figures.bankLines === 0
      ? null
      : Math.round((figures.bankLinesAllocated / figures.bankLines) * 100);

  return (
    <article
      className={`card admin-card${selected ? ' selected' : ''}`}
      data-testid={`administration-${administration.id}`}
    >
      <div className="admin-card-head">
        <div className="avatar">{administration.name.slice(0, 2).toUpperCase()}</div>
        <div className="grow">
          <b>{administration.name}</b>
          <small>
            {connection === null ? null : (
              <>
                <span>{PROVIDER_LABELS[connection.provider] ?? connection.provider}</span> ·{' '}
              </>
            )}
            {administration.country} · {administration.currency}
          </small>
        </div>
        {connection === null || state === null ? (
          <span className="badge">No connection</span>
        ) : (
          <span className={`badge ${state.tone}`} title={state.detail}>{state.label}</span>
        )}
      </div>

      {administration.paused ? (
        <p className="callout warn small" style={{ margin: 0 }}>
          <span className="badge warn">Paused</span>{' '}
          {administration.pausedReason === null ? null : <span>{administration.pausedReason}</span>}
        </p>
      ) : null}

      <div className="admin-card-trend">
        <Trend figures={figures} error={figuresError} />
      </div>

      <div className="admin-stats">
        <div>
          <span>Open</span>
          {work === undefined ? (
            <b className="muted">…</b>
          ) : work.error !== null ? (
            /* Never a zero. "Nothing is waiting" and "we could not ask" are
               different answers, and only one of them is good news. */
            <small className="muted">{work.error}</small>
          ) : open === 0 ? (
            <small className="muted">Up to date</small>
          ) : (
            <button type="button" className="table-action" onClick={onOpenWork}>
              {open} {open === 1 ? 'item' : 'items'} to review
            </button>
          )}
        </div>
        <div>
          <span>Confirmed</span>
          <b>{figures === undefined ? '…' : format.count(figures.confirmedThisMonth)}</b>
        </div>
        <div>
          <span>Allocated</span>
          <b title={figures === undefined ? undefined : `${figures.bankLinesAllocated} of ${figures.bankLines} imported bank lines`}>
            {figures === undefined ? '…' : allocatedShare === null ? '—' : `${allocatedShare}%`}
          </b>
        </div>
      </div>

      <div className="admin-card-foot">
        <span className="muted small">
          {/* ch.02: "Display last successful sync separately from last attempt."
              This is the successful one, and never having synced is a fact worth
              showing plainly. */}
          {administration.paused ? null : <span className="badge ok">Active</span>} Last successful sync:{' '}
          <span>{administration.lastSuccessfulSyncAt === null ? 'Never' : format.dateTime(administration.lastSuccessfulSyncAt)}</span>
          {/* Which implementation is behind this connection. A capability claim
              is about a connector VERSION, so without this the claims are
              unattributable. */}
          {connection?.connectorVersion == null ? null : ` · connector ${connection.connectorVersion}`}
        </span>
        {connection === null ? null : <Capabilities administration={administration} />}
        <AdministrationSettings
          token={workspace.token}
          tenantId={workspace.membership.tenantId}
          administrationId={administration.id}
          canEdit={workspace.membership.role === 'owner' || workspace.membership.role === 'agency_operator'}
        />
        <button type="button" className="btn s" onClick={onOpenWork}>
          Open work queue
        </button>
      </div>
    </article>
  );
}

const PROVIDER_LABELS: Readonly<Record<string, string>> = { eboekhouden: 'e-Boekhouden' };

/**
 * What this connection can do, from the connector's own recorded evidence.
 *
 * ch.06 asks for capability evidence on this screen and, until the connector
 * registry existed, there was none to ask for. Three rules govern how it is
 * shown, and each of them is the manifest's own distinction rather than a
 * presentation choice.
 *
 * NOTHING HERE IS A CONTROL. A manifest is a DESCRIPTION: it says what this
 * connector version claims and what each claim rests on. The server decides
 * nothing from it — the adapter refuses at send time and the release gates bind
 * the evidence a scope was tested against — so a button whose enablement came
 * from this list would teach a reader that the list is a gate.
 *
 * `unverified` IS ITS OWN ANSWER. It means nobody has run that operation
 * against a real administration. Folded into "supported" it is a claim with no
 * evidence; folded into "not offered" it is a refusal nobody decided.
 *
 * NULL IS NOT AN EMPTY LIST. "This build has no connector for that provider"
 * and "that connector can do nothing" are different sentences, and only one of
 * them is true.
 */
function Capabilities({
  administration,
}: {
  readonly administration: Administration;
}): ReactNode {
  const capabilities = administration.capabilities;

  if (capabilities === null) {
    return (
      <p className="muted small">
        This build has no connector for {administration.connection?.provider}, so what it can do is
        not known here.
      </p>
    );
  }

  const supported = capabilities.filter((capability) => capability.support === 'supported');
  const unverified = capabilities.filter((capability) => capability.support === 'unverified');

  return (
    <details>
      <summary>
        {supported.length} of {capabilities.length} operations
        {unverified.length === 0 ? '' : `, ${unverified.length} unverified`}
      </summary>
      <ul className="note-list">
        {capabilities.map((capability) => (
          <li key={capability.operation}>
            <b>{capability.operation.replace(/_/g, ' ')}</b>{' '}
            <span className={`badge ${SUPPORT_TONE[capability.support]}`}>
              {SUPPORT_LABEL[capability.support]}
            </span>
            {capability.testedAt === undefined ? (
              <small className="muted"> · never run against the provider</small>
            ) : (
              <small className="muted"> · last exercised {capability.testedAt}</small>
            )}
            <ul>
              {capability.constraints.map((constraint) => (
                <li key={constraint}>{constraint}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </details>
  );
}

/** ch.03's three-valued word, unlocalised on the wire and localised here. */
const SUPPORT_LABEL: Readonly<Record<string, string>> = {
  supported: 'Supported',
  unsupported: 'Not offered',
  unverified: 'Unverified',
};

const SUPPORT_TONE: Readonly<Record<string, string>> = {
  supported: 'ok',
  unsupported: '',
  // Not 'ok' and not an error. A reviewer must read it as "nobody knows".
  unverified: 'warn',
};

/** Weeks with bookings before the ten-week trend is drawn (the sparkline rule, by week). */
const TREND_MIN_WEEKS = 5;

/** Ten weekly bars of confirmed bookings, with the numbers in text beside them. */
function Trend({
  figures,
  error,
}: {
  readonly figures: AdministrationFigures['administrations'][number] | undefined;
  readonly error: string | null;
}): ReactNode {
  if (figures === undefined) return <span className="muted">{error ?? '…'}</span>;
  const weeks = figures.weeks;
  const max = Math.max(1, ...weeks.map((week) => week.confirmed));
  const sum = weeks.reduce((total, week) => total + week.confirmed, 0);
  return (
    <div style={{ minWidth: 120 }} data-testid={`trend-${figures.administrationId}`}>
      {sum === 0 ? (
        <span className="muted small">None confirmed in ten weeks</span>
      ) : weeks.filter((week) => week.confirmed > 0).length < TREND_MIN_WEEKS ? (
        // Fewer active weeks than a trend needs: say what there is instead of
        // drawing one bar beside nine empty ones.
        <span className="muted small">
          {format.count(sum)} confirmed so far · the trend appears once {TREND_MIN_WEEKS} weeks have bookings
        </span>
      ) : (
        <>
          <div className="trend" aria-hidden="true">
            {weeks.map((week) => (
              <span
                key={week.weekStart}
                className={week.confirmed === 0 ? 'zero' : undefined}
                style={{ height: `${Math.max(6, (week.confirmed / max) * 100)}%` }}
                title={`Week of ${week.weekStart}: ${week.confirmed}`}
              />
            ))}
          </div>
          <small className="muted">
            {figures.confirmedThisMonth} this month · {sum} in ten weeks
          </small>
          <span className="visually-hidden">
            Confirmed per week, oldest first: {weeks.map((week) => week.confirmed).join(', ')}
          </span>
        </>
      )}
    </div>
  );
}
